import React, { useState, useEffect, useRef } from "react";
import { HiArrowRight } from "react-icons/hi";
import Navbar from "./Components/Navbar";
import Footer from "./Components/Footer";
import { useLanguage } from "../../contexts/LanguageContext";

// ─── Teks UI statis (bilingual) ────────────────────────────────────────────────
const UI_TEXT = {
  EN: {
    // Profile
    profileLabel: "Our Profile",
    profileTitle: "Who We Are",
    profileBody: [
      "Rumoh Pangan Aceh (RPA) is a non-profit institution committed to improving food security in Aceh through a sustainable approach. We support local farmers in producing agricultural products by implementing sustainable farming systems and helping them access fair markets to obtain reasonable selling prices.",
      "Additionally, RPA acts as a bridge between communities with surplus food and those in need, to reduce food loss and waste. We are also active in educating the public on the importance of nutritious food consumption and ensuring access to healthy, nutritious food for those who need it.",
    ],

    // History
    historyLabel: "Our Journey",
    historyTitle: "How RPA Came to Be",
    historyPara1: "Rumoh Pangan Aceh (RPA) was founded on June 16, 2024 as a social and community empowerment institution operating in the fields of food security, food loss and food waste reduction, and empowerment of local farmers in Aceh. RPA was born from the experience of Rivan Rinaldi when he joined the Young Southeast Asian Leaders Initiative (YSEALI PFP) program at Farm Alliance of Baltimore, United States.",
    historyPara2: "In the program, Rivan learned how NGOs, food banks, and local communities support farmers through farmers' markets and various community food security programs. This experience became the inspiration to build a food movement that supports local farmers, reduces food waste, and strengthens food security in Aceh. After returning to Indonesia, Rivan invited colleagues to join — including Fauzi Arja, Andri Munazar, Puteri Handika, and Muhammad Haikal.",
    historyPara3: "On the suggestion of Andri Munazar, the initiating team then invited prominent Acehnese figures to jointly build the foundation of this institution. On June 16, 2024, a meeting was held that became the milestone for the establishment of Yayasan Rumoh Pangan Aceh. The meeting was attended and supported by prominent Acehnese figures, including Mustafa Abubakar, Adron Yusuf, Cut Soraya, Hardianto Zainal, Said Mustafa, Yarmen Dinamika, Amri Fathmi, Ibu Rama, and Ibu Siti. Through deliberation in that meeting, those present were entrusted to serve as trustees, management board, and supervisors of the foundation.",
  },

  ID: {
    profileLabel: "Profil Kami",
    profileTitle: "Siapa Kami",
    profileBody: [
      "Rumoh Pangan Aceh (RPA) adalah lembaga non-profit yang berkomitmen untuk meningkatkan ketahanan pangan (food security) di Aceh melalui pendekatan berkelanjutan. Kami mendukung petani lokal dalam memproduksi hasil pertanian dengan menerapkan sistem pertanian yang berkelanjutan (sustainable agriculture) serta membantu mereka mengakses pasar yang adil agar memperoleh harga jual yang layak.",
      "Selain itu, RPA juga berperan sebagai penghubung antara masyarakat yang memiliki surplus makanan dengan mereka yang membutuhkan, guna mengurangi susut dan sisa pangan (food loss and waste reduction). Kami juga aktif dalam mengedukasi masyarakat mengenai pentingnya konsumsi makanan bergizi serta memastikan akses terhadap makanan sehat dan bernutrisi bagi mereka yang membutuhkan.",
    ],

    historyLabel: "Perjalanan Kami",
    historyTitle: "Bagaimana RPA Terbentuk",
    historyPara1: "Rumoh Pangan Aceh (RPA) didirikan pada tanggal 16 Juni 2024 sebagai lembaga sosial dan pemberdayaan masyarakat yang bergerak di bidang ketahanan pangan, pengurangan food loss dan food waste, serta pemberdayaan petani lokal di Aceh. Berdirinya RPA berawal dari pengalaman Rivan Rinaldi saat mengikuti program Young Southeast Asian Leaders Initiative (YSEALI PFP) di Farm Alliance of Baltimore, Amerika Serikat.",
    historyPara2: "Dalam program tersebut, Rivan mempelajari bagaimana NGO, food bank, dan komunitas lokal mendukung petani melalui pasar tani dan berbagai program ketahanan pangan masyarakat. Pengalaman tersebut kemudian menjadi inspirasi untuk membangun gerakan pangan yang mendukung petani lokal, mengurangi pemborosan pangan, serta memperkuat ketahanan pangan di Aceh. Setelah kembali ke Indonesia, Rivan mengajak beberapa rekan untuk bergabung, di antaranya Fauzi Arja, Andri Munazar, Puteri Handika, dan Muhammad Haikal.",
    historyPara3: "Atas masukan dari Andri Munazar, tim inisiator kemudian mengajak sejumlah tokoh Aceh untuk bersama-sama membangun fondasi lembaga ini. Pada 16 Juni 2024, diselenggarakan pertemuan yang menjadi tonggak awal berdirinya Yayasan Rumoh Pangan Aceh. Pertemuan tersebut dihadiri dan didukung oleh sejumlah tokoh Aceh, di antaranya Mustafa Abubakar, Adron Yusuf, Cut Soraya, Hardianto Zainal, Said Mustafa, Yarmen Dinamika, Amri Fathmi, Ibu Rama, dan Ibu Siti. Melalui hasil musyawarah, para peserta ditetapkan untuk mengemban peran sebagai pembina, pengurus, dan pengawas yayasan.",
  },
};

// ─── useInView ─────────────────────────────────────────────────────────────────
const useInView = (ref, threshold = 0.1) => {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref, threshold]);
  return inView;
};

// ─── Profile Section ───────────────────────────────────────────────────────────
const ProfileSection = () => {
  const { language } = useLanguage();
  const tx = UI_TEXT[language];
  const ref = useRef(null);
  const inView = useInView(ref);

  return (
    <section ref={ref} className="bg-[#f7f3ec] py-24 px-4 md:px-8">
      <div className="container max-w-7xl mx-auto">
        <div className="gap-16 items-start">

          {/* Kiri — teks */}
          <div style={{ opacity: inView ? 1 : 0, transform: inView ? "translateY(0)" : "translateY(28px)", transition: "all 0.9s ease" }}>
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px w-10 bg-[#c9862a]" />
              <span className="text-[#c9862a] text-xs font-bold tracking-[4px] uppercase">{tx.profileLabel}</span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-[#1a1a18] leading-tight mb-8">
              {tx.profileTitle}
            </h2>

            {/* Divider */}
            <div className="flex items-center gap-4 mb-8">
              <div className="flex-1 h-px bg-[#e8e0d0]" />
              <div className="w-2 h-2 bg-[#c9862a] rotate-45" />
              <div className="flex-1 h-px bg-[#e8e0d0]" />
            </div>

            {tx.profileBody.map((para, i) => (
              <p key={i} className="text-[#4a4a44] text-[15px] leading-[1.9] mb-5 text-justify">{para}</p>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// ─── History Section ───────────────────────────────────────────────────────────
const HistorySection = () => {
  const { language } = useLanguage();
  const tx = UI_TEXT[language];
  const ref = useRef(null);
  const inView = useInView(ref, 0.05);

  return (
    <section ref={ref} className="bg-white py-6 px-4 md:px-6">
      <div className="max-w-[1400px] mx-auto">
        <div className="bg-[#1a4a35] px-8 md:px-16 lg:px-20 py-16 relative overflow-hidden">

          {/* Decorative watermark */}
          <div className="absolute bottom-0 right-0 font-serif text-[160px] font-bold text-white/[0.035] leading-none select-none pointer-events-none">
            SEJARAH
          </div>

          {/* Header */}
          <div
            className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-6"
            style={{ opacity: inView ? 1 : 0, transform: inView ? "translateY(0)" : "translateY(24px)", transition: "all 0.8s ease" }}
          >
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-px w-10 bg-[#c9862a]" />
                <span className="text-[#c9862a] text-xs font-bold tracking-[4px] uppercase">{tx.historyLabel}</span>
              </div>
              <h2 className="font-serif text-4xl md:text-5xl font-bold text-white leading-tight">
                {tx.historyTitle}
              </h2>
            </div>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-14">
            <div className="flex-1 h-px bg-white/10" />
            <div className="w-2 h-2 bg-[#c9862a] rotate-45" />
            <div className="flex-1 h-px bg-white/10" />
          </div>

          {/* 2-kolom: narasi kiri, timeline kanan */}
          <div className="gap-14 items-start">

            {/* Kolom kiri: narasi lengkap */}
            <div
              className="space-y-5"
              style={{ opacity: inView ? 1 : 0, transform: inView ? "translateY(0)" : "translateY(24px)", transition: "all 0.8s ease 0.1s" }}
            >
              {[tx.historyPara1, tx.historyPara2, tx.historyPara3].map((para, i) => (
                <p key={i} className="text-white/70 text-sm leading-[1.9] text-justify">{para}</p>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ─── Page ──────────────────────────────────────────────────────────────────────
const AboutUs = () => (
  <main className="overflow-x-hidden font-homepage bg-white">
    <Navbar />
    {/* <section id="hero"><Hero /></section> */}
    <section id="profile"><ProfileSection /></section>
    <section id="history"><HistorySection /></section>
    <Footer />
  </main>
);

export default AboutUs;